import { useState, type ComponentProps } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { RunDetail, RunSummary } from '@shared/types/factory';
import { isFeedbackFlow } from '@shared/run-mode';
import { ExternalLink } from '@/components/ExternalLink';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { StatusBadge } from '@/components/StatusBadge';
import { RunLimitBadge } from '@/components/tasks/PickupSkip';
import { useLoader } from '@/hooks/use-loader';
import { describeApiError } from '@/lib/api-errors';
import { formatRelative } from '@/lib/formatters';
import { getRun } from '@/lib/runs-api';
import { cn } from '@/lib/utils';
import { runPath } from '@/navigation/project-paths';
import { CloseRunButton, RunClosedBadge } from './CloseRunButton';
import { RunProgressBar } from './RunProgressBar';
import { RunStepList } from './RunStepList';
import { FEEDBACK_RUN_META, foldedSteps, isRunActive, liveRunMeta, RUN_STATUS_META, runAccent, runRef, runStatusMeta } from './run-status';

/**
 * SF-T-155: marks a run that took an earlier run's open pull request over: a new agent pushing to that PR's
 * branch rather than a branch of its own. On the task's runs and the run's page.
 */
export function RunTakeoverBadge({ takenOver, size = 'sm' }: { takenOver: NonNullable<RunSummary['taken_over']>; size?: 'sm' | 'default' }) {
  return (
    <StatusBadge
      tone="neutral"
      size={size}
      shape="square"
      data-hint={`A new agent took over ${runRef(takenOver.run_number)}'s open pull request and pushes to its branch`}
      data-testid="run-taken-over"
    >
      Took over {runRef(takenOver.run_number)}'s PR
    </StatusBadge>
  );
}

/** The latest run's steps, as often as its own page does. */
const STEPS_POLL_MS = 3000;

export interface TaskRunsListProps {
  /** The project's slug: each run opens /p/<slug>/runs/<number>. */
  projectSlug: string;
  /** The task's runs, newest first (`useTaskRuns`); undefined while the first load runs. */
  runs: RunSummary[] | undefined;
  /** Why they couldn't load (`describeApiError`), shown when there are none to show. */
  error?: string | null;
  onRetry?: () => void;
  /** Called as a run's link is followed (the task pop-up closes). */
  onNavigate?: () => void;
  /** The run just started from this task (T-64): once it's in the list, its card scrolls into view and lights up briefly. */
  highlight?: number | null;
  /** Preview only (the /dev/ui gallery): the newest run's detail (its steps and events), instead of loading it. */
  latest?: RunDetail;
  /**
   * SF-T-167: people who can start runs: Retry setup on the newest run when its worktree setup failed
   * (`setup_failed`), starting a new run on its branch and machine. Resolves once that's done or refused.
   */
  onRetrySetup?: (run: RunSummary) => Promise<void>;
  /**
   * SF-T-279: people who can start runs: Close run on the newest run (with its whole thread), called once it's closed.
   * Closed runs show a Closed badge either way.
   */
  onClosed?: () => void;
  /** The gallery's stand-in for the close request. */
  closeFn?: ComponentProps<typeof CloseRunButton>['closeFn'];
}

/**
 * A task's runs for the task detail. The newest one on top, with a link to its page, how far it's
 * got, the step it's on or the step that failed (all its steps a click away, T-72) and its latest event, kept up to date while it runs; then the earlier ones, newest
 * first: number, status (with the step a running run is on), PR and when it started, the whole row
 * opening the run's page. The caller loads and polls the list (`useTaskRuns`), so the timeline's
 * run links share the same live statuses.
 */
export function TaskRunsList({ projectSlug, runs, error, onRetry, onNavigate, highlight, latest, onRetrySetup, onClosed, closeFn }: TaskRunsListProps) {
  if (!runs && error) return <LoadError compact title="Couldn't load this task's runs" error={error} onRetry={onRetry} />;
  if (!runs) return <LoadingState inline label="Loading runs…" />;
  if (!runs.length) return <p className="text-xs text-muted-foreground">No runs yet.</p>;

  return (
    <div className="space-y-3">
      {/* Keyed on the run, so a new one mounts afresh and its highlight plays once. */}
      <LatestRun
        key={runs[0].id}
        run={runs[0]}
        projectSlug={projectSlug}
        given={latest}
        onNavigate={onNavigate}
        highlighted={highlight != null && highlight === runs[0].number}
        onRetrySetup={onRetrySetup}
        onClosed={onClosed}
        closeFn={closeFn}
      />
      {runs.length > 1 ? <RunList runs={runs.slice(1)} projectSlug={projectSlug} onNavigate={onNavigate} preview={latest} /> : null}
    </div>
  );
}

/**
 * Brings the run just started into view as it mounts (the task pop-up or panel scrolls to it). Its top,
 * since the card grows once its steps load; scroll-mt keeps it (and the Runs heading) clear of the phone pop-up's pinned bar and title.
 */
function scrollIntoView(el: HTMLElement | null) {
  el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

/** The newest run: its progress, steps and latest event, polled while it's going. */
function LatestRun({
  run,
  projectSlug,
  given,
  onNavigate,
  highlighted = false,
  onRetrySetup,
  onClosed,
  closeFn,
}: {
  run: RunSummary;
  projectSlug: string;
  given?: RunDetail;
  onNavigate?: () => void;
  highlighted?: boolean;
  onRetrySetup?: (run: RunSummary) => Promise<void>;
  onClosed?: () => void;
  closeFn?: ComponentProps<typeof CloseRunButton>['closeFn'];
}) {
  const active = isRunActive(run.status);
  const loader = useLoader(
    (signal) => getRun(run.id, { signal, project: projectSlug }),
    [run.id, projectSlug],
    {
      enabled: !given,
      pollMs: STEPS_POLL_MS,
      pollWhile: (res) => (res?.data?.run ? isRunActive(res.data.run.status) : active),
    }
  );
  // The detail can be a poll ahead of the list (which polls every 5 s while a run is going).
  const detail = given ?? loader.data?.data?.run;
  const shown = detail ?? run;
  const lastEvent = detail?.events.at(-1);
  const [showAll, setShowAll] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const stepsShown = detail ? (showAll ? detail.steps : foldedSteps(detail)) : [];
  return (
    <section
      // Not in the gallery's preview, which would jump the whole page to it.
      ref={highlighted && !given ? scrollIntoView : undefined}
      className={cn('space-y-2 rounded-md border p-3', runAccent(shown.status)?.item, highlighted && 'scroll-mt-24 animate-run-started motion-reduce:animate-none')}
      aria-label={`Latest run ${runRef(run.number)}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-medium">{runRef(run.number)}</span>
        {/* SF-T-209: a parked live run says it waits for you. */}
        <StatusBadge meta={runStatusMeta({ status: shown.status, live_parked_at: run.live_parked_at, pause: run.pause })} size="sm" dot={isRunActive(shown.status)} />
        {isFeedbackFlow(run.flow_id) ? <StatusBadge meta={FEEDBACK_RUN_META} size="sm" shape="square" /> : null}
        {run.live && !run.live_parked_at && isRunActive(shown.status) ? <StatusBadge meta={liveRunMeta(run)} size="sm" shape="square" /> : null}
        {run.taken_over ? <RunTakeoverBadge takenOver={run.taken_over} /> : null}
        {run.closed ? <RunClosedBadge closed={run.closed} /> : null}
        {shown.machine ? (
          <span className="text-xs text-muted-foreground" data-hint="The fleet machine doing this run">
            on {shown.machine.name}
          </span>
        ) : null}
        {shown.limit_warning && isRunActive(shown.status) ? <RunLimitBadge warning={shown.limit_warning} /> : null}
        {shown.pr_url ? (
          <ExternalLink href={shown.pr_url} size="xs" variant="muted">
            Pull request
          </ExternalLink>
        ) : null}
        <span className="ml-auto flex items-center gap-3">
          {onClosed ? <CloseRunButton run={run} project={projectSlug} size="xs" onClosed={onClosed} closeFn={closeFn} /> : null}
          <Link to={runPath(projectSlug, run.number)} onClick={onNavigate} className="text-xs font-medium hover:underline">
            Open run
          </Link>
        </span>
      </div>
      {/* Once it's finished, its steps say how it ended. */}
      {isRunActive(shown.status) ? <RunProgressBar run={shown} /> : null}
      {detail?.error ? <p className="text-xs text-danger-strong">{detail.error}</p> : null}
      {onRetrySetup && run.setup_failed && shown.status === 'failed' ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Button
            size="sm"
            variant="outline"
            loading={retrying}
            data-hint="Starts a new run on this branch and machine: the agent sets the worktree up again, then starts work"
            onClick={() => {
              setRetrying(true);
              void onRetrySetup(run).finally(() => setRetrying(false));
            }}
          >
            Retry setup
          </Button>
          <span className="text-2xs text-muted-foreground">Fix what stopped it first: the error above names the step and its output.</span>
        </div>
      ) : null}
      {detail?.steps.length ? (
        <>
          {/* T-72: folded to the step it's on (or the one that failed); the toggle shows them all. */}
          {stepsShown.length ? (
            <RunStepList
              // Remounts on folding, so a step's own toggles start closed again.
              key={showAll ? 'all' : 'current'}
              steps={stepsShown}
              compact
              // Not in the gallery's preview, whose run isn't on any machine.
              agentActivity={detail.t3_thread_id && !given ? { runId: detail.id, project: projectSlug, live: isRunActive(detail.status) } : null}
            />
          ) : null}
          {stepsShown.length < detail.steps.length || showAll ? (
            <button
              type="button"
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              aria-expanded={showAll}
              onClick={() => setShowAll(!showAll)}
            >
              <ChevronRight className={cn('size-3.5 transition-transform', showAll && 'rotate-90')} aria-hidden />
              {showAll ? 'Show fewer steps' : `Show all ${detail.steps.length} steps`}
            </button>
          ) : null}
        </>
      ) : null}
      {!detail && !given && loader.data?.error ? (
        <LoadError compact title="Couldn't load this run's steps" error={describeApiError(loader.data)} onRetry={loader.reload} />
      ) : null}
      {lastEvent ? (
        <p className="text-2xs text-muted-foreground">
          <span className="tabular-nums">{formatRelative(lastEvent.at)}</span> · <span className="break-words">{lastEvent.message}</span>
        </p>
      ) : null}
    </section>
  );
}

/** The earlier runs, newest first, each with its machine; the row opens the run's page, its chevron its steps here (T-72). */
function RunList({ runs, projectSlug, onNavigate, preview }: { runs: RunSummary[]; projectSlug: string; onNavigate?: () => void; preview?: RunDetail }) {
  return (
    <ul className="divide-y overflow-hidden rounded-md border text-sm" aria-label="Earlier runs">
      {runs.map((run) => (
        <EarlierRun key={run.id} run={run} projectSlug={projectSlug} onNavigate={onNavigate} preview={preview} />
      ))}
    </ul>
  );
}

function EarlierRun({ run, projectSlug, onNavigate, preview }: { run: RunSummary; projectSlug: string; onNavigate?: () => void; preview?: RunDetail }) {
  const [open, setOpen] = useState(false);
  return (
    <li className={cn(runAccent(run.status)?.item)}>
      <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 hover:bg-muted/50 has-[:focus-visible]:bg-muted/50">
        <button
          type="button"
          className="relative z-10 -ml-1 rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} ${runRef(run.number)}'s steps`}
          data-hint={open ? 'Hide its steps' : 'Show its steps here'}
          onClick={() => setOpen(!open)}
        >
          <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} aria-hidden />
        </button>
        {/* The link stretches over the row (after:inset-0); the chevron and PR link sit above it. */}
        <Link
          to={runPath(projectSlug, run.number)}
          onClick={onNavigate}
          className="font-medium after:absolute after:inset-0 hover:underline focus-visible:underline focus-visible:outline-none"
          data-hint={`Open ${runRef(run.number)}`}
        >
          {runRef(run.number)}
        </Link>
        <StatusBadge meta={RUN_STATUS_META[run.status]} size="sm" />
        {isFeedbackFlow(run.flow_id) ? <StatusBadge meta={FEEDBACK_RUN_META} size="sm" shape="square" /> : null}
        {run.taken_over ? <RunTakeoverBadge takenOver={run.taken_over} /> : null}
        {run.closed ? <RunClosedBadge closed={run.closed} /> : null}
        {isRunActive(run.status) && run.current_step ? (
          <span className="min-w-0 truncate text-xs text-muted-foreground">{run.current_step_name ?? run.current_step}</span>
        ) : null}
        {run.pr_url ? (
          <ExternalLink href={run.pr_url} size="xs" variant="muted" className="relative z-10">
            Pull request
          </ExternalLink>
        ) : null}
        <span className="ml-auto text-2xs text-muted-foreground">
          {run.machine ? `${run.machine.name} · ` : ''}
          {formatRelative(run.started_at ?? run.created_at)}
        </span>
      </div>
      {open ? <EarlierRunSteps run={run} projectSlug={projectSlug} preview={preview} /> : null}
    </li>
  );
}

/** An earlier run's steps, loaded when its row is opened. */
function EarlierRunSteps({ run, projectSlug, preview }: { run: RunSummary; projectSlug: string; preview?: RunDetail }) {
  // Polled while it's going, as the newest run is: two runs on a task can overlap.
  const loader = useLoader((signal) => getRun(run.id, { signal, project: projectSlug }), [run.id, projectSlug], {
    enabled: !preview,
    pollMs: STEPS_POLL_MS,
    pollWhile: (res) => (res?.data?.run ? isRunActive(res.data.run.status) : isRunActive(run.status)),
  });
  // The gallery's preview: the sample run's steps, not a request for a run that doesn't exist.
  const detail = preview ? { ...preview, status: run.status, error: null, t3_thread_id: null } : loader.data?.data?.run;
  return (
    <div className="space-y-2 px-3 pb-3">
      {detail?.error ? <p className="text-xs text-danger-strong">{detail.error}</p> : null}
      {detail ? (
        detail.steps.length ? (
          <RunStepList
            steps={detail.steps}
            compact
            agentActivity={detail.t3_thread_id ? { runId: detail.id, project: projectSlug, live: isRunActive(detail.status) } : null}
          />
        ) : (
          <p className="text-xs text-muted-foreground">No steps.</p>
        )
      ) : loader.data?.error ? (
        <LoadError compact title="Couldn't load this run's steps" error={describeApiError(loader.data)} onRetry={loader.reload} />
      ) : (
        <LoadingState inline label="Loading its steps…" />
      )}
    </div>
  );
}
